/**
 * TroubleshootingGuide - User-Friendly Help Component
 *
 * Institutional-grade troubleshooting guide with:
 * - Clear, readable steps with bright text
 * - Visual indicators and icons
 * - Action buttons for each step
 * - Support contact options
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Collapse,
    IconButton,
    Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ComputerIcon from '@mui/icons-material/Computer';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BuildIcon from '@mui/icons-material/Build';

import { QBRIDGE_DOWNLOAD_URL, QBRIDGE_VERSION, QBRIDGE_SIZE } from '../../../../config';

const TroubleshootingGuide = ({
    colors,
    onRetry,
    isDetecting = false,
    variant = 'qbridge' // 'qbridge' or 'mt5'
}) => {
    const [expanded, setExpanded] = useState(true);

    // Troubleshooting steps for QBridge
    const qbridgeSteps = [
        {
            number: 1,
            title: 'Check Hidden Icons',
            description: 'Click the ^ (up arrow) in your system tray to see hidden icons. QBridge may be hiding there.',
            icon: <KeyboardArrowUpIcon />,
            action: null,
            actionLabel: null,
            tip: 'Look for the green Q icon in the expanded tray area'
        },
        {
            number: 2,
            title: 'Restart QBridge',
            description: 'Open the Start Menu, type "QBridge", and click to launch it.',
            icon: <RestartAltIcon />,
            action: onRetry,
            actionLabel: 'Retry Detection',
            tip: 'After starting QBridge, wait a few seconds and click Retry'
        },
        {
            number: 3,
            title: 'Reinstall QBridge',
            description: `If QBridge still won't start, download and reinstall the latest version.`,
            icon: <DownloadIcon />,
            action: () => window.open(QBRIDGE_DOWNLOAD_URL, '_blank'),
            actionLabel: `Download v${QBRIDGE_VERSION}`,
            tip: `File size: ${QBRIDGE_SIZE} | Windows 10/11`
        }
    ];

    // Troubleshooting steps for MT5
    const mt5Steps = [
        {
            number: 1,
            title: 'Open MetaTrader 5',
            description: 'Make sure MetaTrader 5 is running on your computer.',
            icon: <ComputerIcon />,
            action: null,
            actionLabel: null,
            tip: 'Launch MT5 from your desktop shortcut or Start Menu'
        },
        {
            number: 2,
            title: 'Login to Your Account',
            description: 'Ensure you are logged into your trading account in MT5.',
            icon: <SearchIcon />,
            action: onRetry,
            actionLabel: 'Retry Detection',
            tip: 'Check the bottom-left corner of MT5 for connection status'
        },
        {
            number: 3,
            title: 'Check Connection',
            description: 'Verify that MT5 shows "Connected" to your broker server.',
            icon: <BuildIcon />,
            action: null,
            actionLabel: null,
            tip: 'If disconnected, go to File → Login to Trade Account'
        }
    ];

    const steps = variant === 'qbridge' ? qbridgeSteps : mt5Steps;
    const title = variant === 'qbridge' ? 'QBridge Not Detected?' : 'MT5 Not Detected?';

    return (
        <Box
            sx={{
                width: '100%',
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                overflow: 'hidden'
            }}
        >
            {/* ═══════════════════════════════════════════════════════════════════
                HEADER
                ═══════════════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 2,
                    borderBottom: expanded ? `1px solid ${colors.border}` : 'none',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: colors.surfaceHover
                    }
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BuildIcon sx={{ color: colors.info || '#3B82F6', fontSize: '1.25rem' }} />
                    <Typography
                        sx={{
                            color: colors.text,
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            fontFamily: "'Inter', sans-serif"
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                <IconButton
                    size="small"
                    sx={{ color: colors.textMuted }}
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* ═══════════════════════════════════════════════════════════════════
                CONTENT
                ═══════════════════════════════════════════════════════════════════ */}
            <Collapse in={expanded}>
                <Box sx={{ p: 2.5 }}>
                    <Typography
                        sx={{
                            color: colors.textMuted,
                            fontSize: '0.9375rem',
                            fontFamily: "'Inter', sans-serif",
                            mb: 2.5,
                            lineHeight: 1.5
                        }}
                    >
                        Try these steps to resolve the issue:
                    </Typography>

                    {/* Steps */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {steps.map((step) => (
                            <Box
                                key={step.number}
                                sx={{
                                    background: `${colors.infoBg || 'rgba(59, 130, 246, 0.08)'}`,
                                    border: `1px solid ${colors.info || '#3B82F6'}20`,
                                    borderRadius: '10px',
                                    p: 2
                                }}
                            >
                                {/* Step Header */}
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '8px',
                                            background: colors.info || '#3B82F6',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#FFFFFF',
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            fontFamily: "'Inter', sans-serif",
                                            flexShrink: 0
                                        }}
                                    >
                                        {step.number}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            sx={{
                                                color: colors.text,
                                                fontSize: '0.9375rem',
                                                fontWeight: 600,
                                                fontFamily: "'Inter', sans-serif",
                                                lineHeight: 1.3
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: colors.textSecondary,
                                                fontSize: '0.9375rem',
                                                fontFamily: "'Inter', sans-serif",
                                                lineHeight: 1.6,
                                                mt: 0.5
                                            }}
                                        >
                                            {step.description}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Tip */}
                                {step.tip && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            pl: 5.5,
                                            mt: 1
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: colors.textMuted,
                                                fontSize: '0.875rem',
                                                fontFamily: "'Inter', sans-serif",
                                                fontStyle: 'italic',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {step.tip}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Action Button */}
                                {step.action && step.actionLabel && (
                                    <Box sx={{ pl: 5.5, mt: 1.5 }}>
                                        <Button
                                            size="small"
                                            onClick={step.action}
                                            disabled={isDetecting}
                                            startIcon={
                                                step.actionLabel.includes('Retry') ? (
                                                    <RefreshIcon sx={{ fontSize: '1rem' }} />
                                                ) : step.actionLabel.includes('Download') ? (
                                                    <DownloadIcon sx={{ fontSize: '1rem' }} />
                                                ) : null
                                            }
                                            sx={{
                                                px: 2,
                                                py: 0.75,
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                fontFamily: "'Inter', sans-serif",
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                color: colors.info || '#3B82F6',
                                                border: `1px solid ${colors.info || '#3B82F6'}40`,
                                                background: 'transparent',
                                                lineHeight: 1.5,
                                                '&:hover': {
                                                    background: `${colors.info || '#3B82F6'}15`,
                                                    borderColor: colors.info || '#3B82F6'
                                                },
                                                '&.Mui-disabled': {
                                                    color: colors.textDim,
                                                    borderColor: colors.border
                                                }
                                            }}
                                        >
                                            {step.actionLabel}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {/* ═══════════════════════════════════════════════════════════════
                        SUPPORT SECTION
                        ═══════════════════════════════════════════════════════════════ */}
                    <Box
                        sx={{
                            mt: 3,
                            pt: 2.5,
                            borderTop: `1px solid ${colors.border}`
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <SupportAgentIcon sx={{ color: colors.primary, fontSize: '1.125rem' }} />
                            <Typography
                                sx={{
                                    color: colors.text,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    fontFamily: "'Inter', sans-serif"
                                }}
                            >
                                Still Having Trouble?
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                color: colors.textMuted,
                                fontSize: '0.9375rem',
                                fontFamily: "'Inter', sans-serif",
                                mb: 2,
                                lineHeight: 1.5
                            }}
                        >
                            Our support team is here to help. Reach out through any of these channels:
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button
                                size="small"
                                onClick={() => window.open('https://discord.gg/quantx', '_blank')}
                                startIcon={
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                                    </svg>
                                }
                                endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    fontFamily: "'Inter', sans-serif",
                                    textTransform: 'none',
                                    borderRadius: '6px',
                                    color: '#5865F2',
                                    border: '1px solid #5865F240',
                                    background: 'transparent',
                                    lineHeight: 1.5,
                                    '&:hover': {
                                        background: '#5865F215',
                                        borderColor: '#5865F2'
                                    }
                                }}
                            >
                                Discord Community
                            </Button>

                            <Button
                                size="small"
                                onClick={() => window.open('mailto:info@qforgex.com', '_blank')}
                                startIcon={<EmailIcon sx={{ fontSize: '1rem' }} />}
                                endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    fontFamily: "'Inter', sans-serif",
                                    textTransform: 'none',
                                    borderRadius: '6px',
                                    color: colors.textSecondary,
                                    border: `1px solid ${colors.border}`,
                                    background: 'transparent',
                                    lineHeight: 1.5,
                                    '&:hover': {
                                        background: colors.surfaceHover,
                                        borderColor: colors.borderLight
                                    }
                                }}
                            >
                                Email Support
                            </Button>
                        </Box>

                        <Typography
                            sx={{
                                color: colors.textDim,
                                fontSize: '0.75rem',
                                fontFamily: "'Inter', sans-serif",
                                mt: 1.5,
                                lineHeight: 1.5
                            }}
                        >
                            Response time: &lt; 4 hours during business hours
                        </Typography>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default TroubleshootingGuide;
